import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Input, Table, message, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import MentorshipAPI from '~/services/MentorshipAPI';
import type { UserProfile } from '~/models/UserProfile';
import FileInput from '~/components/FileInput';
import UserProfileAPI from '~/services/UserProfileAPI';
import { NetworkResponseDisplay, type NetworkResponse } from '~/components/NetworkResponse';
import MentorshipSessionsList from '../mentorship/MentorshipSessionsList';

const UserProfileView = () => {
  const { username } = useParams<{ username: string }>();
  const [form] = Form.useForm();
  const [mentor, setMentor] = useState<any>(null);
  const [userProfileLoggedIn, setuserProfileLoggedIn] = useState<UserProfile|null>(null);
  const [prices, setPrices] = useState<any[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [networkResponse, setNetworkResponse] = useState<NetworkResponse>({title: "", type: null});

  useEffect(() => {
    const fetchMentor = async () => {
      try {
        setNetworkResponse({title: "Loading mentor", type: "loading"});
        const response = await MentorshipAPI.listMentors(`?username=${username}`);
        const fetchedMentor = response.data.results[0];
        setMentor(fetchedMentor);
        setNetworkResponse({title: "", type: null});
        
        if (fetchedMentor) {
          form.setFieldsValue({
            description: fetchedMentor.description,
            linkedin_url: fetchedMentor.linkedin_url,
          });
          setPrices(fetchedMentor.prices || []);
        }
      } catch (err) {
        setNetworkResponse({title: "Failed to load mentor profile", type: "error"});
        console.error('Error fetching mentor:', err);
      }
    };

    const checkOwnership = () => {
      try {
        const atilaUser = JSON.parse(localStorage.getItem('atilaUser') || '{}');
        setuserProfileLoggedIn(atilaUser?.user_profile);
        setIsOwner(atilaUser?.user_profile?.username === username);
      } catch {
        setIsOwner(false);
      }
    };

    fetchMentor();
    checkOwnership();
  }, [username, form]);

  const onChangeProfilePicture = (event: { target: { value: any; }; }) => {

    if (!userProfileLoggedIn) return;
    UserProfileAPI.patch(
        {
            profile_pic_url: event.target.value,
        }, userProfileLoggedIn.user)
        .then(res => {
            setuserProfileLoggedIn(res.data);
        })
        .catch(err=> {
            console.log({err});
        });
};

  const addPriceRow = () => {
    setPrices([...prices, { minutes: '', price: '', schedule_url: '' }]);
  };

  const handleRemovePriceRow = (index: number) => {
  const newPrices = [...prices];
  newPrices.splice(index, 1);
  setPrices(newPrices);
};

  const handlePriceChange = (value: any, index: number, key: string) => {
    const newPrices = [...prices];
    newPrices[index][key] = value;
    setPrices(newPrices);
  };

  const columns = [
    {
      title: 'Minutes',
      dataIndex: 'minutes',
      render: (text: any, record: any, index: number) => (
        <Input
          value={text}
          onChange={e => handlePriceChange(e.target.value, index, 'minutes')}
        />
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (text: any, record: any, index: number) => (
        <Input
          value={text}
          onChange={e => handlePriceChange(e.target.value, index, 'price')}
        />
      )
    },
    {
      title: 'Schedule URL',
      dataIndex: 'schedule_url',
      render: (text: any, record: any, index: number) => (
        <Input
          value={text}
          onChange={e => handlePriceChange(e.target.value, index, 'schedule_url')}
        />
      )
    },
    {
      title: 'Remove',
      key: 'remove',
      render: (_: any, __: any, index: number) => (
      <Button danger onClick={() => handleRemovePriceRow(index)}>
        Remove
      </Button>
      )
    }
  ];

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...mentor,
        ...values,
        prices,
      };
      
      if (mentor) {

        setNetworkResponse({title: "saving changes to mentor profile", type: "loading"});
        MentorshipAPI
            .patchMentor({mentor: payload!})
            .then((res: any) => {
                const { data } = res;
                setMentor(data);
                setNetworkResponse({title: "Mentor profile updated successfully", type: "success"});
            })
      }
    } catch (error) {

        setNetworkResponse({title: "Failed to save mentor profile", type: "error"});
    }
  };

  const createMentorProfile = () => {
    MentorshipAPI.createMentor(userProfileLoggedIn?.user!)
    .then((res: any) => {
        const { data } = res;
        setMentor(data);
    })
    message.success('Mentor profile created successfully');
  }

  const onChangeTabs = (key: string) => {
  console.log(key);
};

  const items: TabsProps['items'] = [
  {
    key: '1',
    label: (isOwner ? 'Edit' : 'View') +  ' Mentor Profile',
    children: <div>
      

      {isOwner && mentor ? (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} />
          </Form.Item>

          <FileInput title={"Profile Picture"}
            type={"image"}
            keyName={"profile_pic_url"}
            filePath={`user-profile-pictures/${mentor.user.id}`}
            onChangeHandler={onChangeProfilePicture} />

          <Form.Item name="linkedin_url" label="LinkedIn URL">
            <Input />
          </Form.Item>

          <Form.Item label="Prices">
            <Table
              dataSource={prices}
              columns={columns}
              pagination={false}
              rowKey={(record, index) => (index ?? 0).toString()}
            />
            <Button onClick={addPriceRow} style={{ marginTop: 8 }}>Add Price Row</Button>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
                Update Profile
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <>
          {mentor ? (
            <div>
            <h2>{mentor.user.first_name} {mentor.user.last_name}</h2>
            <img src={mentor.user.profile_pic_url} alt="avatar" style={{ width: '100px' }} />
              <h3>Description</h3>
              <p>{mentor.description}</p>
              
              <h3>LinkedIn</h3>
              <p>
                <a href={mentor.linkedin_url} target="_blank" rel="noopener noreferrer">
                  {mentor.linkedin_url}
                </a>
              </p>
              
              <div style={{ marginTop: 24 }}>
                <h3>Schedule a Session:</h3>
                <ul>
                  {mentor.prices?.map((price: any, index: number) => (
                    <li key={index}>
                      <a href={price.schedule_url} target="_blank" rel="noopener noreferrer">
                        {price.minutes} min - ${price.price}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            networkResponse.type !== "loading" ? <p>No mentor profile found for this user.</p>: ""
          )}
        </>
      )}
      
      {!mentor && isOwner && networkResponse.type !== "loading" && (
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={createMentorProfile}>
            Create Mentor Profile
          </Button>
        </div>
      )}
      </div>,
  },
  {
    key: '2',
    label: 'View Mentor Sessions',
    children: <MentorshipSessionsList username={username!} />,
  },
];

  return (
    <div className='p-4 m-4'>
    <Tabs defaultActiveKey="1" items={items} onChange={onChangeTabs} />
      <NetworkResponseDisplay response={networkResponse} />
    </div>
  );
};

export default UserProfileView;